import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import React from 'react';
import { Link } from 'react-router-dom';

import type { LimitedRole, PracticeUsageStats } from '../../hooks/usePracticeUsageStats';

export interface PlanLimitAlertProps {
  stats: PracticeUsageStats | null;
}

const ROLE_LABELS: Record<LimitedRole, string> = {
  clients: 'clients',
  practitioners: 'practitioners',
  admins: 'admins',
};

const countsFor = (stats: PracticeUsageStats, role: LimitedRole): [number, number] => {
  switch (role) {
    case 'clients':
      return [stats.clientsUsed, stats.clientsEntitled];
    case 'practitioners':
      return [stats.practitionersUsed, stats.practitionersEntitled];
    default:
      return [stats.adminsUsed, stats.adminsEntitled];
  }
};

const joinLabels = (labels: string[]): string =>
  labels.length < 2
    ? labels.join('')
    : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;

/**
 * PlanLimitAlert (HA-453)
 *
 * Warns when a role is over the plan's limit (members beyond it keep access, but new invites for
 * that role are refused) or exactly at it. Admin counts leave out the practice owner.
 */
const PlanLimitAlert: React.FC<PlanLimitAlertProps> = ({ stats }) => {
  const overLimit = stats?.overLimit ?? [];
  const atLimit = stats?.atLimit ?? [];

  if (!stats || (overLimit.length === 0 && atLimit.length === 0)) {
    return null;
  }

  const upgrade = (
    <Button component={Link} to="/subscription" color="inherit" size="small">
      Upgrade plan
    </Button>
  );

  if (overLimit.length > 0) {
    const details = overLimit.map((role) => {
      const [used, entitled] = countsFor(stats, role);

      return `${used} ${ROLE_LABELS[role]} on a plan for ${entitled}`;
    });
    const roles = joinLabels(overLimit.map((role) => ROLE_LABELS[role]));
    const invites = stats.gracePeriodEnd
      ? `You can still invite ${roles} until ${new Date(stats.gracePeriodEnd).toLocaleDateString()}, while your grace period lasts.`
      : `New ${roles} can't be invited until you upgrade or remove some.`;

    return (
      <Alert severity="warning" action={upgrade} sx={{ mb: 3 }} data-testid="plan-limit-alert">
        <AlertTitle>Your practice is over its plan limit</AlertTitle>
        {`${joinLabels(details)}. Everyone already in your practice keeps access. ${invites}`}
      </Alert>
    );
  }

  return (
    <Alert severity="info" action={upgrade} sx={{ mb: 3 }} data-testid="plan-limit-alert">
      {`Your plan's ${joinLabels(atLimit.map((role) => ROLE_LABELS[role]))} limit is reached. Upgrade to invite more.`}
    </Alert>
  );
};

export default PlanLimitAlert;
